/**
 * The fleet, one row per machine, **two badges per row**.
 *
 * `web/shapes/devices.md` §6: the lifecycle column and the heartbeat column are
 * separate columns because they are separate verdicts that can disagree. There is no
 * cell anywhere in this file that combines them.
 *
 * ── The pending row is the one that decides the design ──────────────────────
 *
 * Task 17 made a pending credential valid immediately for authentication and
 * heartbeat but **not for event submission**, so a pending machine that is powered on
 * reads `online` — truthfully — while every batch it sends is refused with
 * `WORKFORCE_DEVICE_NOT_APPROVED` and accumulates in the Agent's local queue. A green
 * dot would tell an administrator the rollout is fine. It is not: that machine has
 * landed nothing, and if it is revoked before it is approved, that queue is exactly
 * what revoke destroys.
 *
 * So a pending row carries **both badges and a sentence**, never a summary of the
 * three.
 *
 * ── The heartbeat column ─────────────────────────────────────────────────────
 *
 * A timestamp with its age, or the words "chưa từng báo cáo". Never a blank, never a
 * zero age: `last_heartbeat_at: null` is its own case, and rendering it as an empty
 * cell is the same class of mistake as rendering an outage as a zero.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstantWithAge } from '../../i18n/datetime';
import type { Device } from '../../api/types';
import { isAwaitingApproval, readingOf } from '../../devices/fleet';
import { LivenessBadge, StatusBadge } from './DeviceBadges';

/**
 * The person a machine is bound to — **Luật Không In Mã Máy**, fix round 2 (R3).
 *
 * `Device.employee_id` is an achau identifier (`admin.ts` renders it from
 * `e.achau_user_id`), and the column printed it: `990103`, in a monospace face, as
 * the only thing this screen ever said about the human being held responsible for a
 * device. A number nobody in the building can read back to a name is not a sentence.
 *
 * So the name wins whenever the screen knows it, and when it does not — the
 * directory read was refused, or the person is off the page it fetched — the visible
 * cell says so in words and the id stays in the DOM for the machine: `title` for a
 * hover, `.wp-visually-hidden` for a screen reader and for anyone grepping a bug
 * report. Never a blank, never the bare number.
 */
export function DeviceEmployee({
  employeeId,
  names,
  testId,
}: {
  readonly employeeId: string | null;
  readonly names: ReadonlyMap<string, string>;
  readonly testId?: string;
}): ReactNode {
  const { t } = useI18n();
  if (employeeId === null) {
    return <span className="wp-muted">{t('devices.table.unassigned')}</span>;
  }
  const name = names.get(employeeId);
  const code = t('devices.table.employeeCode', { id: employeeId });
  return name === undefined ? (
    <span title={code} {...(testId === undefined ? {} : { 'data-testid': testId })}>
      <span className="wp-muted">{t('devices.table.unnamed')}</span>
      <span className="wp-visually-hidden">{code}</span>
    </span>
  ) : (
    <span title={code} {...(testId === undefined ? {} : { 'data-testid': testId })}>
      {name}
      <span className="wp-visually-hidden">{code}</span>
    </span>
  );
}

export function DeviceTable({
  devices,
  total,
  timezone,
  selectedId,
  onSelect,
  employeeNames,
}: {
  readonly devices: readonly Device[];
  readonly total: number;
  readonly timezone: string;
  readonly selectedId: string | null;
  readonly onSelect: (deviceId: string) => void;
  /** achau employee id → full name, from the directory the screen reads. */
  readonly employeeNames: ReadonlyMap<string, string>;
}): ReactNode {
  const { t, locale } = useI18n();

  return (
    <section className="wp-section" aria-labelledby="k5-table-heading">
      <h2 className="wp-section__title" id="k5-table-heading">
        {t('nav.devices')}
      </h2>
      <div className="wp-table-scroll">
        <table className="wp-table" data-testid="k5-table">
          <thead>
            <tr>
              <th scope="col">{t('devices.table.hostname')}</th>
              <th scope="col">{t('devices.table.employee')}</th>
              <th scope="col">{t('devices.table.status')}</th>
              <th scope="col">{t('devices.table.liveness')}</th>
              <th scope="col">{t('devices.table.heartbeat')}</th>
              <th scope="col">{t('devices.table.open')}</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => {
              const name = device.hostname ?? device.device_id;
              const reading = readingOf(device.liveness, device.last_heartbeat_at);
              return (
                <tr
                  key={device.device_id}
                  data-testid={`k5-row-${device.device_id}`}
                  {...(selectedId === device.device_id ? { 'aria-current': 'true' } : {})}
                >
                  <th scope="row">
                    <span className="wp-mono">
                      {device.hostname ?? t('devices.table.noHostname')}
                    </span>
                    {isAwaitingApproval(device.status) ? (
                      // Beside the two badges, never instead of either.
                      <p
                        className="wp-small wp-muted"
                        data-testid={`k5-pending-${device.device_id}`}
                      >
                        {t('devices.pending.body')}
                      </p>
                    ) : null}
                  </th>
                  <td>
                    <DeviceEmployee
                      employeeId={device.employee_id}
                      names={employeeNames}
                      testId={`k5-employee-${device.device_id}`}
                    />
                  </td>
                  <td>
                    <StatusBadge status={device.status} testId={`k5-status-${device.device_id}`} />
                  </td>
                  <td>
                    <LivenessBadge reading={reading} testId={`k5-liveness-${device.device_id}`} />
                  </td>
                  <td data-testid={`k5-heartbeat-${device.device_id}`}>
                    {device.last_heartbeat_at === null ? (
                      // The words, not a blank and not an age of zero.
                      <span className="wp-muted">{t('devices.reading.never_reported')}</span>
                    ) : (
                      formatInstantWithAge(
                        device.last_heartbeat_at,
                        locale,
                        timezone,
                        t('common.never'),
                      )
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="wp-button wp-button--quiet"
                      onClick={() => {
                        onSelect(device.device_id);
                      }}
                      aria-label={t('devices.table.openLabel', { name })}
                      data-testid={`k5-open-${device.device_id}`}
                    >
                      {t('devices.table.open')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {devices.length < total ? (
        <p className="wp-small wp-muted" data-testid="k5-table-truncated">
          {t('devices.table.truncated', { shown: devices.length, total })}
        </p>
      ) : null}
    </section>
  );
}
